import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { AlarmClock } from 'lucide-react';
const STORAGE_KEY = 'boxing_coach_ready_countdown_target';
const ONE_DAY = 24 * 60 * 60 * 1000;
function getInitialTarget(): number {
  if (typeof window === 'undefined') return Date.now() + ONE_DAY;
  const storedTarget = window.localStorage.getItem(STORAGE_KEY);
  if (storedTarget) {
    const parsedTarget = Number.parseInt(storedTarget, 10);
    if (!Number.isNaN(parsedTarget) && parsedTarget > Date.now()) return parsedTarget;
  }
  const target = Date.now() + ONE_DAY;
  try {
    window.localStorage.setItem(STORAGE_KEY, target.toString());
  } catch {
    /* storage unavailable */
  }
  return target;
}
function pad(value: number) {
  return value.toString().padStart(2, '0');
}
export function CountdownTimer() {
  const [target, setTarget] = useState<number | null>(null);
  const [remaining, setRemaining] = useState(ONE_DAY);
  useEffect(() => {
    setTarget(getInitialTarget());
  }, []);
  useEffect(() => {
    if (!target) return;
    const tick = () => {
      const difference = target - Date.now();
      if (difference <= 0) {
        const nextTarget = Date.now() + ONE_DAY;
        try {
          window.localStorage.setItem(STORAGE_KEY, nextTarget.toString());
        } catch {
          /* storage unavailable */
        }
        setTarget(nextTarget);
        setRemaining(ONE_DAY);
        return;
      }
      setRemaining(difference);
    };
    tick();
    const interval = window.setInterval(tick, 1000);
    return () => window.clearInterval(interval);
  }, [target]);
  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor(remaining / (1000 * 60) % 60);
  const seconds = Math.floor(remaining / 1000 % 60);
  const elapsed = ONE_DAY - remaining;
  const progress = Math.min(90, 65 + elapsed / ONE_DAY * 25);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    duration: 0.3,
    ease: [0.23, 1, 0.32, 1]
  }} className="mt-10 max-w-3xl mx-auto bg-paper border border-silver-light rounded-2xl p-6 md:p-8 shadow-[0_10px_30px_-20px_rgba(21,23,26,0.25)]" data-id="element-44" data-mp="2igh6f3mxcj9c">
      <div className="text-center" data-id="element-45" data-mp="2jaq6eenhhgjk">
        <div className="flex items-center justify-center gap-2 text-accent-dark font-bold tracking-wider uppercase text-sm md:text-base mb-6" data-id="element-46" data-mp="2jdw0677256lm">
          <AlarmClock size={20} data-id="element-47" data-mp="29e56g7qbmvml" />
          <span data-id="element-48" data-mp="29f6gc8vlpc2q">Launch price ends in:</span>
        </div>
        <div className="flex items-center justify-center gap-3 md:gap-5 mb-5" data-id="element-49" data-mp="24mwscye1qm33">
          <TimeBox value={pad(hours)} label="hrs" data-id="element-50" data-mp="1znskqc6rswev" />
          <span className="text-3xl md:text-5xl font-bold text-accent" data-id="element-51" data-mp="1zolgyj83s3ir">:</span>
          <TimeBox value={pad(minutes)} label="min" data-id="element-52" data-mp="1vni3229m8hk6" />
          <span className="text-3xl md:text-5xl font-bold text-accent" data-id="element-53" data-mp="1qndxh7a8x78q">:</span>
          <TimeBox value={pad(seconds)} label="sec" data-id="element-54" data-mp="1qpgl5knxeoaa" />
        </div>
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase text-graphite mb-8" data-id="element-55" data-mp="1lpc6m06kw3mp">
          The 5 bonus guides are included at this launch price.
        </p>
        <div className="space-y-2" data-id="element-56" data-mp="64tmrmo251z8">
          <div className="flex justify-between text-[11px] md:text-xs font-bold uppercase tracking-wider" data-id="element-57" data-mp="63rz8ipuun0m">
            <span className="text-graphite" data-id="element-58" data-mp="b57lge3rqzkl">Remaining launch spots</span>
            <span className="text-accent-dark" data-id="element-59" data-mp="f4zz3580rvwt">Only a few left</span>
          </div>
          <div className="w-full h-2.5 bg-cream rounded-full overflow-hidden border border-silver-light" data-id="element-60" data-mp="3elody4vhsxxh">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: `${progress}%`
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="h-full bg-accent rounded-full" data-id="element-61" data-mp="3jjga5b36767h" />
          </div>
          <p className="text-right text-[11px] md:text-xs text-graphite-light font-medium" data-id="element-62" data-mp="2vtlvqp37d9l4">
            {Math.round(progress)}% of spots filled
          </p>
        </div>
      </div>
    </motion.div>;
}
function TimeBox({
  value,
  label
}: {
  value: string;
  label: string;
}) {
  return <div className="bg-ink rounded-xl px-4 md:px-6 py-3 md:py-4 min-w-[80px] md:min-w-[110px]" data-id="element-63" data-mp="2bt2dcoqylrtu">
      <div className="flex items-baseline justify-center gap-1" data-id="element-64" data-mp="2qhyo4tkehiy5">
        <span className="text-4xl md:text-5xl font-display font-bold text-white tabular-nums" data-id="element-65" data-mp="2qj9qcoy76in5">
          {value}
        </span>
        <span className="text-xs md:text-sm font-medium text-white/60" data-id="element-66" data-mp="2zhlnzaxhegp3">{label}</span>
      </div>
    </div>;
}